import { setCurrentTime, setIsClickInnerCard, setIsFullScreen, setIsLive, setLock, setPlay } from "../slices/controllerSlice";
import { progressBar, videoTag } from "../../helpers/constants";
import { backwardForwardLog, catchUpLoadingLog, fullscreenLog, lockLog, videoTimelineLog } from "../../logs/logs";
import store from "../store";
import { setIsComplexPortrait, setPlayerParams, setVideoElementSizeInfo, setVideoTimelines } from "../slices/videoDataSlice";
import { conditions } from "../../helpers/conditions/conditions";
import { ConditionName } from "../../interfaces/enums";
import { apiCaller } from "../../services/apiCaller";
import { API } from "../../services/services";
import { setUserSettings } from "../slices/userSlice";
import { sendFluentLog } from "../../logs/fluentdLogger";
import { clearRedux, convertB64ToFile, responseValidator, utf8_to_b64 } from "../../helpers/helpers";
import axios from "axios";
import { setClearPopoversSlice } from "../slices/popoversSlice";
import { handleContinuousPlayback } from "ui/src/playerComponents/continuousPlayback/continuousePlayback";
import { findFirstVideoInRecommendationList } from "./videoData.actions";

export const handlePlayPause = (event: any) => {
    const dispatch = store.dispatch;
    if (event === null || event.target.nodeName !== "INPUT") {
        if (event !== null) {
            event.stopPropagation();
            event.preventDefault();
        }
        videoTag().paused ? videoTag().play() : videoTag().pause();
        dispatch(setPlay(!videoTag().paused));
    }
};

export const handlePlayPauseInDOM = (type: boolean) => {
    const dispatch = store.dispatch;
    if (type) {
        videoTag().play();
        dispatch(setPlay(true));
        return true;
    } else {
        videoTag().pause();
        dispatch(setPlay(false));
        return false;
    }
};

export const handleChangeSoundVolume = (volume: number) => {
    const dispatch = store.dispatch;
    const { settings } = store.getState().user;
    dispatch(setUserSettings({ ...settings, volume }));
    if (conditions(ConditionName.isIOS)) videoTag().muted = !videoTag().muted;
    else videoTag().volume = volume / 100;
};

export const handleIsLive = () => {
    const dispatch = store.dispatch;
    const { hlsLiveSyncPosition } = store.getState().controller;
    handleVideoTimeline(videoTag().currentTime, hlsLiveSyncPosition);
    videoTag().currentTime = hlsLiveSyncPosition;
    dispatch(setCurrentTime(hlsLiveSyncPosition));
    if (progressBar()) progressBar().style.width = "100%";
    dispatch(setIsLive(true));
    const timer = Date.now();
    videoTag()
        .play()
        .then(() => sendFluentLog(catchUpLoadingLog(Date.now() - timer)));
};

export const handleBackwardForward = (type: string, forwardBackwardCount: number = 15, event?: any) => {
    if (event && event.target.nodeName === "INPUT") return;
    const dispatch = store.dispatch;
    if (type === "touchBackward") type = "backward";
    else if (type === "touchForward") type = "forward";
    sendFluentLog(backwardForwardLog(type));
    let currentTime = 0;
    if (type === "backward") {
        if (forwardBackwardCount === 0) currentTime = 0;
        else currentTime = videoTag().currentTime - forwardBackwardCount;
    } else if (type === "forward") {
        if (forwardBackwardCount === videoTag().duration) currentTime = forwardBackwardCount;
        else currentTime = videoTag().currentTime + forwardBackwardCount;
    }
    if (conditions(ConditionName.isLiveParam)) dispatch(setCurrentTime(currentTime));
    if (!conditions(ConditionName.isStaticFormat)) handleVideoTimeline(videoTag().currentTime, videoTag().currentTime + forwardBackwardCount);
    videoTag().currentTime = currentTime;
};

export const handleFullScreen = () => {
    const dispatch = store.dispatch;
    const isFullScreen = document.fullscreenElement;
    if (conditions(ConditionName.isIOS)) {
        if ((videoTag() as any).webkitEnterFullScreen) {
            dispatch(setPlay(false));
            // Toggle fullscreen in Safari for iPad
            (videoTag() as any).webkitEnterFullScreen();
        } else {
            dispatch(setClearPopoversSlice());
            sendFluentLog(fullscreenLog("fullScreen"));
        }
    } else {
        if (isFullScreen) {
            dispatch(setIsFullScreen(false));
            document.exitFullscreen();
            if (conditions(ConditionName.isTouchScreen) && conditions(ConditionName.isParamsPortrait)) dispatch(setIsComplexPortrait(true));
        } else {
            dispatch(setClearPopoversSlice());
            document.documentElement.requestFullscreen();
            dispatch(setIsFullScreen(true));
            sendFluentLog(fullscreenLog("fullScreen"));
            if (conditions(ConditionName.isTouchScreen) && conditions(ConditionName.isParamsPortrait)) dispatch(setIsComplexPortrait(false));
        }
    }
};

export const handleLockOrientation = () => {
    if (conditions(ConditionName.isTouchScreen) && !conditions(ConditionName.isIOS)) {
        if (conditions(ConditionName.isLandscape) && !conditions(ConditionName.isFullScreen)) (window.screen.orientation as any).lock("portrait");
        else (window.screen.orientation as any).lock("landscape");
    }
};

export const handleLock = (type: string) => {
    const dispatch = store.dispatch;
    dispatch(setLock(type === "lock"));
    sendFluentLog(lockLog(type));
};

export const handleVideoTimeline = (currentTimeValue: number, movedTimeValue?: number) => {
    if (conditions(ConditionName.isSendTimelineLog)) {
        const dispatch = store.dispatch;
        const { videoTimelines } = store.getState().videoData;
        let currentTime = Math.floor(currentTimeValue);
        let movedTime = Math.floor(movedTimeValue!);
        if (videoTimelines.length === 0) {
            dispatch(setVideoTimelines([currentTime >= videoTag().duration && !conditions(ConditionName.isLive) ? movedTime! : currentTime]));
        } else {
            if (videoTimelines[0] < currentTime)
                apiCaller({ url: API().videoTimelineLog, method: "post", data: videoTimelineLog(videoTimelines[0], currentTime) });
            if (movedTime && movedTime >= videoTag().duration) dispatch(setVideoTimelines([]));
            else dispatch(setVideoTimelines([movedTime!]));
        }
    }
};

export const handleSnapShot = async (setLoading: (loading: boolean) => void) => {
    let canvas: any = document.getElementById("snapshot-canvas");
    let video: any = document.querySelector("#player-tag");
    if (canvas) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
        const imgData = canvas.toDataURL("image/JPEG");
        const { data, status } = await apiCaller({ url: API().live.tempLink });
        if (responseValidator(status)) {
            let file = convertB64ToFile(imgData, "snapshot.jpeg");
            const response = await axios({
                url: data.url,
                method: "put",
                headers: { "Content-Type": "" },
                data: file
            });

            if (response.status >= 200 && response.status < 300) {
                if (conditions(ConditionName.hasOnSnapshotParams)) store.getState().videoData.params.onSnapshot!(utf8_to_b64(data.image_name));
                setLoading(false);
            }
        }
    }
};

export const handleVideoElementSizeInfo = () => {
    const dispatch = store.dispatch;
    const element: any = document.getElementById("video-area")?.getBoundingClientRect();
    element.height = (element.width * 9) / 16;
    if (element) dispatch(setVideoElementSizeInfo(element));
};

export const handleClickVideoCard = (videoId?: string | null, videoType?: string | null, isBackButtonClick?: boolean) => {
    const dispatch = store.dispatch;
    const { params, recommendationList } = store.getState().videoData;
    let id = videoId;
    let type = videoType;
    if (recommendationList && recommendationList.length && (!videoId || !videoType)) {
        const recommendationVideo = findFirstVideoInRecommendationList();
        id = videoId ? videoId : recommendationVideo.id;
        type = videoType ? videoType : recommendationVideo.type;
    }
    if (videoTag()) {
        handleVideoTimeline(videoTag().currentTime);
        if (!conditions(ConditionName.isEnded)) handleContinuousPlayback();
    }
    if (id && type) {
        if (params.handleRedirectCards && !isBackButtonClick) params.handleRedirectCards(id, type);
        clearRedux();
        dispatch(setPlayerParams({ ...params, id, type, xToken: params.xToken }));
        dispatch(setIsClickInnerCard(true));
    }
};
